import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  Box,
  BoxProps,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import {
  TableChipCell,
  TableDateCell,
  TableTextCell,
  TableUpdateStatusCell,
} from "@pimo/pimo-components";
import { Currency, Initiative } from "aint-types";
import React, { useState } from "react";

import { InitiativePopup } from "../initative-popup/initative-popup";
import { PhaseSectionsCard } from "./phase-sections-card";

interface InitiativesTableRow {
  function: string;
  phases: {
    count: number;
    phaseName: string;
    phaseColor: string;
    textColor: string;
  }[];
  initiatives: {
    name: string;
    projectStartDate: string;
    monitoringEndDate: string;
    actualBenefits: string;
    plannedBenefits: string;
    phase: {
      body: string;
      /** chip props */
      chipProps?: BoxProps;
    };
    reportingDetails: {
      body: string;
      icon: string;
      chipColor: string;
    };
    initiative?: Initiative;
  }[];
}

export interface InitiativesTableProps {
  functions: InitiativesTableRow[];
  showInterOeRegionField: boolean;
  showBusinessUnitField: boolean;
  showCategoryField: boolean;
  showOeProgramFields: boolean;
  currency?: Currency;
}

export const AintInitiativesTable: PimoReactComponent<
  InitiativesTableProps,
  "toggle",
  boolean
> = ({
  functions,
  fireEvent,
  showBusinessUnitField,
  showInterOeRegionField,
  showCategoryField,
  showOeProgramFields,
  currency,
}) => {
  const [openRowIndex, setOpenRowIndex] = useState<null | number>(null);
  const theme = useTheme();
  const [showPopup, setShowPopup] = useState(false);
  const [initiativeOnPopup, setInitiaiveOnPopup] = useState<Initiative>();
  const handleToggle = (index: number) => {
    const newIndex = openRowIndex === index ? null : index;
    setOpenRowIndex(newIndex);
    fireEvent?.("toggle", newIndex !== null);
  };
  return (
    <TableContainer
      component={Paper}
      sx={{
        backgroundColor: "transparent",
        borderRadius: "12px",
        border: "none",
        boxShadow: "none",
      }}
    >
      <Table
        sx={{
          borderCollapse: "separate",
          borderSpacing: "0px 8px",
          borderRadius: "12px",
        }}
      >
        <TableBody>
          {functions.map((item, index) => (
            <React.Fragment key={index}>
              <Table
                sx={{
                  backgroundColor: "white",
                  borderBottomRightRadius: openRowIndex === index ? 0 : "12px",
                  borderBottomLeftRadius: openRowIndex === index ? 0 : "12px",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                  border: "none",
                  borderCollapse: "separate",
                  borderSpacing: "0px 8px",
                  cursor: "pointer",
                }}
              >
                <TableBody>
                  <TableRow onClick={() => handleToggle(index)}>
                    <TableCell sx={{ border: "none", py: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          minWidth: "230px",
                          maxWidth: "230px",
                          color: "#414141",
                          gap: 2,
                        }}
                      >
                        <Typography variant="subtitle2">Function</Typography>
                        <Typography variant="body2">{item.function}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ border: "none", py: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          color: "#414141",
                          width: 420,
                        }}
                      >
                        <Typography variant="subtitle2">
                          Initiative Phases
                        </Typography>
                        <PhaseSectionsCard phases={item.phases} />
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ border: "0px", py: 0, textAlign: "right" }}
                    >
                      <IconButton>
                        {openRowIndex === index ? (
                          <KeyboardArrowUp />
                        ) : (
                          <KeyboardArrowDown />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <TableRow sx={{ backgroundColor: "white", borderRadius: "12px" }}>
                <TableCell
                  style={{
                    padding: 0,
                    border: "none",
                    borderRadius: "12px",
                  }}
                  colSpan={2}
                >
                  <Collapse
                    in={openRowIndex === index}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Table sx={{ borderRadius: "12px" }}>
                      <TableRow
                        sx={{
                          backgroundColor: "white",
                          borderColor: "#deeff2",
                          borderRadius: "12px",
                          height: "48px",
                        }}
                      >
                        <TableCell
                          sx={{
                            backgroundColor: "white",
                            borderColor: "#deeff2",
                            textAlign: "left",
                            color: theme.palette.primary.contrastText,
                          }}
                        >
                          <Typography variant="subtitle2">
                            Initiative
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: "white",
                            borderColor: "#deeff2",
                            px: 1,
                            color: theme.palette.primary.contrastText,
                          }}
                        >
                          <Typography
                            sx={{ textAlign: "left" }}
                            variant="subtitle2"
                          >
                            Project Start
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: "white",
                            borderColor: "#deeff2",
                            color: theme.palette.primary.contrastText,

                            px: 1,
                          }}
                        >
                          <Typography
                            sx={{ textAlign: "left" }}
                            variant="subtitle2"
                          >
                            Monitoring End
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: "white",
                            borderColor: "#deeff2",
                            color: theme.palette.primary.contrastText,
                          }}
                        >
                          <Typography
                            sx={{ textAlign: "end" }}
                            variant="subtitle2"
                          >
                            Actual Net Benefits
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: "white",
                            borderColor: "#deeff2",
                            color: theme.palette.primary.contrastText,
                          }}
                        >
                          <Typography
                            sx={{ textAlign: "end" }}
                            variant="subtitle2"
                          >
                            Planned Net Benefits
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: "white",
                            borderColor: "#deeff2",
                            color: theme.palette.primary.contrastText,
                          }}
                        >
                          <Typography variant="subtitle2">Phase</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: "white",
                            borderColor: "#deeff2",
                            color: theme.palette.primary.contrastText,
                          }}
                        >
                          <Typography variant="subtitle2">
                            Reporting Status
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableBody
                        sx={{ borderRadius: "12px", cursor: "pointer" }}
                      >
                        {item.initiatives.map((row, subIndex) => (
                          <TableRow
                            sx={{
                              backgroundColor: "white",
                              borderBottom: "1px solid",
                              borderColor: "#deeff2",
                              borderRadius: "12px",
                              color: theme.palette.primary.contrastText,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setShowPopup(true);
                              setInitiaiveOnPopup(row.initiative);
                            }}
                            key={subIndex}
                          >
                            <TableCell
                              sx={{
                                border: "none",
                                py: 1,
                                textAlign: "left",
                                borderBottomLeftRadius: "12px",
                                color: theme.palette.primary.contrastText,
                              }}
                            >
                              <Typography
                                sx={{ borderBottomLeftRadius: "12px" }}
                              >
                                {row.name}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "none",
                                py: 1,
                                color: theme.palette.primary.contrastText,

                                alignItems: "center",
                                justifyContent: "flex-start",
                                px: 1,
                              }}
                            >
                              <TableDateCell
                                cardProps={{
                                  sx: {
                                    p: 0,
                                    alignContent: "right",
                                    alignItems: "right",
                                    justifyContent: "right",
                                    textAlign: "right",
                                    mr: 0,
                                  },
                                }}
                                date={row.projectStartDate}
                                icon={"calendar.svg"}
                                iconSize={"30px"}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "none",
                                color: theme.palette.primary.contrastText,
                                width: "100px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                px: 1,
                              }}
                            >
                              <TableDateCell
                                cardProps={{
                                  sx: {
                                    p: 0,
                                    alignContent: "right",
                                    alignItems: "right",
                                    justifyContent: "right",
                                    textAlign: "right",
                                    mr: 0,
                                  },
                                }}
                                date={row.monitoringEndDate}
                                icon={"calendar.svg"}
                                iconSize={"30px"}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "none",
                                py: 1,
                                color: theme.palette.primary.contrastText,
                                alignItems: "right",
                              }}
                            >
                              <TableTextCell
                                cardProps={{
                                  sx: {
                                    p: 0,
                                    alignContent: "right",
                                    alignItems: "right",
                                    justifyContent: "right",
                                    textAlign: "right",
                                    mr: 0,
                                  },
                                }}
                                containerProps={{
                                  sx: {
                                    alignItems: "right",
                                    justifyContent: "right",
                                  },
                                }}
                                body={row.actualBenefits}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "none",
                                py: 1,
                                color: theme.palette.primary.contrastText,
                                alignItems: "right",
                                alignContent: "right",
                                justifyContent: "right",
                                textAlign: "right",
                              }}
                            >
                              <TableTextCell
                                cardProps={{
                                  sx: {
                                    p: 0,
                                    alignContent: "right",
                                    alignItems: "right",
                                    justifyContent: "right",
                                    textAlign: "right",
                                    mr: 0,
                                  },
                                }}
                                containerProps={{
                                  sx: {
                                    alignItems: "right",
                                    justifyContent: "right",
                                  },
                                }}
                                body={row.plannedBenefits}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "none",
                                py: 1,
                                color: theme.palette.primary.contrastText,
                              }}
                            >
                              <TableChipCell {...row.phase} />
                            </TableCell>
                            <TableCell
                              sx={{
                                border: "none",
                                borderBottomRightRadius: "12px",
                                color: theme.palette.primary.contrastText,
                              }}
                            >
                              <TableUpdateStatusCell
                                {...row.reportingDetails}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
      <InitiativePopup
        open={showPopup}
        initiative={initiativeOnPopup}
        setOpen={setShowPopup}
        showInterOeRegionField={showInterOeRegionField}
        showBusinessUnitField={showBusinessUnitField}
        showCategoryField={showCategoryField}
        showOeProgramFields={showOeProgramFields}
        currency={currency}
      ></InitiativePopup>
    </TableContainer>
  );
};

export default AintInitiativesTable;
